import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AbstractAuditTrailService } from '@iot-platform/audit-trail';
import { CustomEncoder, ENVIRONMENT } from '@iot-platform/core';
import { ApiHelpers, DiffUtils } from '@iot-platform/iot-platform-utils';
import { HistoryLog, HistoryLogDetail } from '@iot-platform/models/audit-trail';
import { CommonApiListResponse, CommonApiRequest, CommonApiResponse, Environment, Pagination } from '@iot-platform/models/common';
import { get } from 'lodash';
import { noop, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

type HistoryLogParams = CommonApiRequest;

@Injectable({
  providedIn: 'root'
})
export class AuditTrailService extends AbstractAuditTrailService<Partial<HistoryLogParams>> {
  protected readonly httpClient: HttpClient = inject(HttpClient);
  protected readonly environment: Environment = inject(ENVIRONMENT);

  get baseUrl() {
    return `${this.environment.api.url}${this.environment.api.endpoints.auditTrail}`;
  }

  getAll(options: Partial<HistoryLogParams>): Observable<CommonApiResponse<HistoryLog, Pagination>> {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    params = params.set('concept', options.additionalOptions?.concept);
    params = params.set('conceptId', options.additionalOptions?.elementId);
    params = params.set('limit', 500);
    return this.httpClient
      .get<CommonApiListResponse<HistoryLog>>(this.baseUrl, { params })
      .pipe(map((response) => ApiHelpers.getHttpResponse<HistoryLog>(response)));
  }

  getById(params: { id: string } & Partial<HistoryLogParams>): Observable<HistoryLogDetail> {
    return this.httpClient.get<HistoryLogDetail>(`${this.baseUrl}/${params.id}`).pipe(
      map((logDetail: HistoryLogDetail) => {
        const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
        const predicate = (a: { v: number | string }, b: { v: number | string }): boolean => a !== b;
        logDetail.updatedAttributes.forEach((attr: string) => {
          const oldVal = get(logDetail, ['oldImage', attr]);
          const newVal = get(logDetail, ['newImage', attr]);
          if (isRecord(oldVal) || isRecord(newVal)) {
            logDetail.oldImage[attr] = { ...DiffUtils.diffObjectsWith(oldVal ?? {}, newVal ?? {}, predicate) };
            logDetail.newImage[attr] = { ...DiffUtils.diffObjectsWith(newVal ?? {}, oldVal ?? {}, predicate) };
          }
        });

        return logDetail;
      })
    );
  }

  exportLogDetails(): void {
    noop();
  }
}
